import {
  Button,
  Checkbox,
  Flex,
  Grid,
  IconAddLine,
  Modal,
  ScreenReaderContent,
  Text,
  TextArea,
} from "@instructure/ui";
import { useTranslation } from "react-i18next";
import React, { useEffect, useState } from "react";
import RichTextEditor from "react-rte";
import { ActionService } from "../../services/ActionService";
import { ExternalService } from "../../services/ExternalService";
import { Files } from "../../services/FilesService";
import { ReflectionService } from "../../services/ReflectionService";
import { fileWriter } from "../../utils/FileUtils";
import { formatToReadableTime } from "../../utils/utils";
import { CommonModal } from "../CommonModal";
import { ERROR, useNotification } from "../CustomHooks/NotificationProvider";
import { EMPTY_RTE } from "../CustomTextArea/CustomRTE";
import { Selector } from "../Selectors/Selector";
import ActionTypesModal from "./ActionTypesModal";

export const ActionEditPanel = ({
  title,
  contextId,
  action,
  openModal,
  setOpenModal,
  handleActionUpdated,
  handleActionCreated,
  attachedReflections,
  external,
  handleClose,
  reflectionList,
  setReflectionList,
  reflectionTemplates,
}) => {
  const { t } = useTranslation();
  const showNotification = useNotification();
  const [actionTypes, setActionTypes] = useState([]);
  const [openActionTypeModal, setOpenActionTypeModal] = useState(false);
  const [editedAction, setEditedAction] = useState(null);
  const [inProgress, setInProgress] = useState(false);
  const [selectedReflections, setSelectedReflections] = useState([]);

  useEffect(() => {
    setEditedAction(action);
    if (action?.id) {
      const reflectionsWithAction = reflectionList.filter((reflection) =>
        reflection.actions.some((actionId) => actionId === action.id)
      );
      setSelectedReflections(reflectionsWithAction);
    } else {
      setSelectedReflections([]);
    }
  }, [action, reflectionList]);

  useEffect(() => {
    const getActionTypes = external
      ? ExternalService.getExternalActionTypes
      : ActionService.getActionTypes;

    getActionTypes()
      .then((actionTypes) => {
        updateActionTypes(actionTypes);
      })
      .catch(() => {
        showNotification(
          t("notifications.general.fetchError", {
            obj: t("actions.actionTypes"),
          }),
          ERROR
        );
      });
  }, [showNotification, external]);

  const updateActionTypes = (actionTypeList) => {
    setActionTypes(
      actionTypeList.map((actionType) => ({
        id: actionType.id,
        label: actionType.name,
      }))
    );
  };

  function handleSelectReflection(reflection, selected) {
    if (selected) {
      setSelectedReflections((prev) =>
        prev.filter((r) => r.ddl_id !== reflection.ddl_id)
      );
    } else {
      setSelectedReflections((prev) => [...prev, reflection]);
    }
  }

  const body = () => {
    return (
      <Modal.Body>
        <Flex wrap="wrap" gap="medium">
          <Flex.Item width="50%">
            <Selector
              label={t("actions.actionType")}
              id="action-type-select"
              options={actionTypes}
              selectHandler={(actionId) => {
                setEditedAction((prevAction) => ({
                  ...prevAction,
                  action_type: actionTypes.find(
                    (action) => action.id === actionId
                  ),
                }));
              }}
              selectedItemId={editedAction?.action_type?.id || null}
            />
          </Flex.Item>
          <Flex.Item align="end">
            <Button
              renderIcon={IconAddLine}
              onClick={() => setOpenActionTypeModal(true)}
            >
              {t("actions.addActionType")}
            </Button>
          </Flex.Item>
          <Flex.Item width="100%">
            <TextArea
              label={t("actions.actionDesc")}
              value={editedAction?.text || ""}
              onChange={(e) =>
                setEditedAction((prevAction) => ({
                  ...prevAction,
                  text: e.target.value,
                }))
              }
            />
          </Flex.Item>
          {reflectionList && reflectionList.length > 0 && (
            <Flex.Item width="100%">
              <Text weight="bold">
                {t("reflections.associatedReflections")}
              </Text>
            </Flex.Item>
          )}
          <Flex.Item shouldGrow margin="0 0 0 small">
            <Grid direction="row" vAlign="middle" rowSpacing="none">
              {attachedReflections?.length === 0 &&
                reflectionList
                  .filter((r) => r.is_draft === false)
                  .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
                  .map((reflection) => {
                    const selected =
                      editedAction?.id &&
                      selectedReflections.some(
                        (r) => r.ddl_id === reflection.ddl_id
                      );
                    return (
                      <Grid.Row key={reflection.ddl_id} hAlign="start">
                        <Grid.Col width="auto">
                          <Checkbox
                            label={
                              <ScreenReaderContent>
                                {t("reflections.reflection")}
                              </ScreenReaderContent>
                            }
                            checked={selected}
                            onChange={() =>
                              handleSelectReflection(reflection, selected)
                            }
                          />
                        </Grid.Col>
                        <Grid.Col>
                          <div
                            style={{
                              marginBottom: "10px",
                              border: "1px solid #ccc",
                              padding: "10px",
                              borderRadius: "5px",
                            }}
                          >
                            <Grid.Row>
                              <Grid.Col>
                                <Text as="p" fontStyle="italic" size="small">
                                  {reflection.metadata.author}
                                  {" - "}
                                  {formatToReadableTime(
                                    new Date(reflection.timestamp)
                                  )}
                                </Text>
                              </Grid.Col>
                            </Grid.Row>
                            <Grid.Row>
                              <Grid.Col>
                                {reflection.from_template ? (
                                  <Flex
                                    wrap="wrap"
                                    gap="medium"
                                    margin="small 0 0 0"
                                  >
                                    {reflection.sections.map((section) => (
                                      <Flex.Item key={section.id} width="100%">
                                        <Flex wrap="wrap" gap="small">
                                          <Flex.Item width="100%">
                                            <Text weight="bold">
                                              {section.header_question}
                                            </Text>
                                          </Flex.Item>
                                          {section.helper_text &&
                                            section.helper_text !==
                                              EMPTY_RTE && (
                                              <Flex.Item>
                                                <RichTextEditor
                                                  className="readOnlyClass"
                                                  value={RichTextEditor.createValueFromString(
                                                    section.helper_text,
                                                    "html"
                                                  )}
                                                  readOnly
                                                />
                                              </Flex.Item>
                                            )}
                                          <Flex.Item padding="0 0 0 large">
                                            <div
                                              style={{
                                                borderLeft: "2px solid #03893D",
                                                paddingLeft: "0.75rem",
                                              }}
                                            >
                                              <RichTextEditor
                                                className="readOnlyClass"
                                                value={RichTextEditor.createValueFromString(
                                                  section.content,
                                                  "html"
                                                )}
                                                readOnly
                                              />
                                            </div>
                                          </Flex.Item>
                                        </Flex>
                                      </Flex.Item>
                                    ))}
                                  </Flex>
                                ) : (
                                  <div style={{ marginTop: "0.75rem" }}>
                                    <RichTextEditor
                                      className="readOnlyClass"
                                      value={RichTextEditor.createValueFromString(
                                        reflection.content,
                                        "html"
                                      )}
                                      readOnly
                                    />
                                  </div>
                                )}
                              </Grid.Col>
                            </Grid.Row>
                          </div>
                        </Grid.Col>
                      </Grid.Row>
                    );
                  })}
              {attachedReflections.map((reflection) => (
                <Grid.Row key={reflection.ddl_id} hAlign="start">
                  <Grid.Col>
                    {reflection.from_template ? (
                      <Text as="p">
                        {
                          reflectionTemplates.find(
                            (t) => t.id === reflection.template_id
                          ).name
                        }
                      </Text>
                    ) : (
                      <RichTextEditor
                        className="readOnlyClass"
                        value={RichTextEditor.createValueFromString(
                          reflection.content,
                          "html"
                        )}
                        readOnly
                      />
                    )}
                  </Grid.Col>
                </Grid.Row>
              ))}
            </Grid>
          </Flex.Item>
        </Flex>
      </Modal.Body>
    );
  };

  async function handleEditAction() {
    try {
      const updateAction = external
        ? ExternalService.updateExternalAction
        : ActionService.updateAction;

      const updatedAction = await updateAction(editedAction);

      if (attachedReflections.length === 0) {
        await handleSetDifActionReflections(selectedReflections, editedAction);
      }
      if (handleActionUpdated) {
        handleActionUpdated(updatedAction);
      }

      setOpenModal(false);
    } catch {
      showNotification(
        t("notifications.general.updateError", {
          obj: t("notifications.action.obj"),
        }),
        ERROR
      );
    } finally {
      setInProgress(false);
    }
  }

  const handleSetDifActionReflections = async (newReflections, action) => {
    // If action is added to reflections
    for (const newReflection of newReflections) {
      const reflection = reflectionList.find(
        (r) => r.ddl_id === newReflection.ddl_id
      );
      if (!reflection.actions.some((actionId) => actionId === action.id)) {
        await handleUpdateActionReflection({
          ...reflection,
          actions: [...reflection.actions, action.id],
        });
      }
    }
    // If action is removed from reflections
    for (const reflection of reflectionList) {
      if (
        reflection.actions.some((actionId) => actionId === action.id) &&
        !newReflections.some((r) => r.ddl_id === reflection.ddl_id)
      ) {
        const newActions = reflection.actions.filter(
          (actionId) => actionId !== action.id
        );
        await handleUpdateActionReflection({
          ...reflection,
          actions: newActions,
        });
      }
    }
  };

  const handleUpdateActionReflection = async (reflection) => {
    const updateReflection = external
      ? ExternalService.addExternalReflectionForContext
      : ReflectionService.addReflectionForContext;

    if (reflection.from_template) {
      const promiseList = reflection.sections.map((section) =>
        updateReflection({
          reflectionId: reflection.ddl_id,
          sectionId: section.id,
          templateId: reflection.template_id,
          contextId: contextId,
          author: reflection.metadata.author,
          actions: reflection.actions,
        })
      );

      const updatedReflections = await Promise.all(promiseList);

      updatedReflections.forEach((updatedReflection, index) => {
        const reflectionFile = fileWriter(
          reflection.sections[index].content,
          `${reflection.sections[index].id}-${index + 1}response.html`,
          "text/html"
        );
        Files.postPreSignedUrl(
          updatedReflection.url,
          updatedReflection.fields,
          reflectionFile
        );
      });

      setReflectionList((prevList) => [
        { ...reflection, timestamp: new Date().toISOString() },
        ...prevList.filter((item) => item.ddl_id !== reflection.ddl_id),
      ]);
    } else {
      const updatedReflection = await updateReflection({
        reflectionId: reflection.ddl_id,
        contextId: contextId,
        author: reflection.metadata.author,
        actions: reflection.actions,
      });

      setReflectionList((prevList) => [
        { ...reflection, timestamp: new Date().toISOString() },
        ...prevList.filter((item) => item.ddl_id !== reflection.ddl_id),
      ]);

      const reflectionFile = fileWriter(
        reflection.content,
        "response.html",
        "text/html"
      );
      Files.postPreSignedUrl(
        updatedReflection.url,
        updatedReflection.fields,
        reflectionFile
      );
    }
  };

  async function addAction() {
    try {
      const createAction = external
        ? ExternalService.createExternalActionForContext
        : ActionService.createActionForContext;
      const newAction = await createAction(editedAction, contextId);
      setEditedAction(newAction);

      if (selectedReflections.length > 0) {
        await handleSetDifActionReflections(selectedReflections, newAction);
      } else {
        await handleSetDifActionReflections(attachedReflections, newAction);
      }

      if (handleActionCreated) {
        handleActionCreated(newAction);
      }
      setOpenModal(false);
    } catch {
      showNotification(
        t("notifications.general.createError", {
          obj: t("notifications.action.obj"),
        }),
        ERROR
      );
    } finally {
      setInProgress(false);
    }
  }

  const onSubmitListener = () => {
    if (editedAction.action_type.id && editedAction.text) {
      setInProgress(true);
      if (editedAction.id) {
        handleEditAction();
      } else {
        addAction();
      }
    } else {
      showNotification(t("actions.addActionError"), ERROR);
    }
  };

  return (
    <>
      <CommonModal
        openModal={openModal}
        setOpenModal={setOpenModal}
        label={title}
        size="medium"
        handleSubmit={onSubmitListener}
        body={body()}
        inProgress={inProgress}
        handleClose={handleClose}
      />
      <ActionTypesModal
        addTypesModalOpen={openActionTypeModal}
        setAddTypesModalOpen={setOpenActionTypeModal}
        handleCloseTypesModal={() => setOpenActionTypeModal(false)}
        setTypes={updateActionTypes}
        external={external}
      />
    </>
  );
};
